/**
 * D11: Teacher Assessment Assignments
 * Source: Teacher portal — "Toets klaarzetten" feature
 */

import { USE_BACKEND, readJson, writeJson } from '../helpers'
import type { TeacherAssignment } from '../../assessment'

const TEACHER_ASSIGNMENTS_KEY = 'teacher_assignments'

/** Get all teacher-created assessment assignments */
export async function getTeacherAssignments(): Promise<
  Record<string, TeacherAssignment>
> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  return readJson<Record<string, TeacherAssignment>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
}

/** Save or update a teacher assessment assignment */
export async function saveTeacherAssignment(
  id: string,
  assignment: TeacherAssignment,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, TeacherAssignment>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
  all[id] = assignment
  writeJson(TEACHER_ASSIGNMENTS_KEY, all)
}

/** Delete a teacher assessment assignment */
export async function deleteTeacherAssignment(id: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, TeacherAssignment>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
  delete all[id]
  writeJson(TEACHER_ASSIGNMENTS_KEY, all)
}

/** Archive a teacher assessment assignment */
export async function archiveTeacherAssignment(id: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, TeacherAssignment>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
  const assignment = all[id]
  if (!assignment) return
  assignment.status = 'archived'
  all[id] = assignment
  writeJson(TEACHER_ASSIGNMENTS_KEY, all)
}

/** Mark an assignment as completed by a specific student */
export async function markAssignmentCompletedByStudent(
  assignmentId: string,
  studentId: string,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
  const assignment = all[assignmentId]
  if (!assignment) return
  const completedBy = (assignment.completedByStudentIds as string[]) || []
  if (!completedBy.includes(studentId)) {
    assignment.completedByStudentIds = [...completedBy, studentId]
    writeJson(TEACHER_ASSIGNMENTS_KEY, all)
  }
}

/**
 * F-AS-R1-ASSIGNED-ASSESSMENT-EXIT (May 2026)
 *
 * Mark an assigned assessment as abandoned by a specific student (closed
 * the tab, hit the exit-confirm in the assessment session, or abandoned a
 * recoverable session). Tracked separately from `completedByStudentIds`
 * so the teacher dashboard can distinguish "not yet started" from
 * "started and gave up". Idempotent — a student already in the list is
 * not re-added. Does NOT touch `completedByStudentIds`; a later
 * successful completion will set that flag and the UI is expected to
 * treat completion as the dominant signal.
 */
export async function markAssignmentAbandonedByStudent(
  assignmentId: string,
  studentId: string,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  const all = readJson<Record<string, Record<string, unknown>>>(
    TEACHER_ASSIGNMENTS_KEY,
    {},
  )
  const assignment = all[assignmentId]
  if (!assignment) return
  const abandoned = (assignment.abandonedByStudentIds as string[]) || []
  if (!abandoned.includes(studentId)) {
    assignment.abandonedByStudentIds = [...abandoned, studentId]
    writeJson(TEACHER_ASSIGNMENTS_KEY, all)
  }
}
